import React, { useEffect, useState } from 'react';

import { params } from '@aline/routes';
import { Alert } from '@mui/material';
import { updateUrlWithoutRefresh } from 'src/helpers/url';

/**
 * Reads the OAuth-callback result on mount, surfaces it as a one-shot
 * banner, then scrubs the query params so a refresh doesn't re-show it.
 *
 * The param key + status strings are shared with the server redirect via
 * `params.channelInstall` so both sides stay in sync.
 */
const PARAM_KEY = params.channelInstall.key;
const STATUS_ADMIN_REQUIRED = params.channelInstall.status.adminRequired;
const STATUS_CONFLICT = params.channelInstall.status.conflict;
const STATUS_SUCCESS = params.channelInstall.status.success;
const STATUS_ERROR = params.channelInstall.status.error;
const STATUS_PENDING = params.channelInstall.status.pending;

type BannerState =
  | { kind: 'none' }
  | { kind: 'success' }
  | { kind: 'pending' }
  | { kind: 'adminRequired' }
  | { kind: 'conflict' }
  | { kind: 'error' };

export function ChannelInstallBanner(): JSX.Element | null {
  const [state, setState] = useState<BannerState>({ kind: 'none' });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get(PARAM_KEY);
    if (
      status !== STATUS_SUCCESS &&
      status !== STATUS_CONFLICT &&
      status !== STATUS_ERROR &&
      status !== STATUS_PENDING &&
      status !== STATUS_ADMIN_REQUIRED
    )
      return;

    setState({ kind: getBannerKind(status) });
    params.delete(PARAM_KEY);
    const qs = params.toString();
    const cleaned = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
    updateUrlWithoutRefresh(cleaned);
  }, []);

  if (state.kind === 'none') return null;

  if (state.kind === 'pending') {
    return (
      <Alert severity="warning" onClose={() => setState({ kind: 'none' })}>
        Organization linked. Teams setup is still pending. Continue setup below.
      </Alert>
    );
  }

  if (state.kind === 'success') {
    return (
      <Alert severity="success" onClose={() => setState({ kind: 'none' })}>
        Connection saved. Check the integration below for any remaining setup steps.
      </Alert>
    );
  }

  if (state.kind === 'conflict') {
    return (
      <Alert severity="warning" onClose={() => setState({ kind: 'none' })}>
        This workspace is already connected to another organization. Contact support to move it.
      </Alert>
    );
  }

  if (state.kind === 'adminRequired') {
    return (
      <Alert severity="warning" onClose={() => setState({ kind: 'none' })}>
        A Microsoft 365 admin has to approve this connection. If this browser is already signed into
        Microsoft, the login screen is skipped and a non-admin account fails immediately. Retry with
        a Global Administrator, or ask your IT admin to click Add to Teams.
      </Alert>
    );
  }

  return (
    <Alert severity="error" onClose={() => setState({ kind: 'none' })}>
      Channel install failed. Try again, or contact support if the problem persists.
    </Alert>
  );
}

function getBannerKind(status: string): Exclude<BannerState['kind'], 'none'> {
  if (status === STATUS_PENDING) return 'pending';
  if (status === STATUS_SUCCESS) return 'success';
  if (status === STATUS_ADMIN_REQUIRED) return 'adminRequired';
  if (status === STATUS_CONFLICT) return 'conflict';
  return 'error';
}
